import React, { createContext, useEffect, useReducer, useRef, useState } from "react";

import { getBootstrapData } from "@swa-ui/bootstrap";
import { FetchOptions, getResponseErrors, isResponseOk } from "@swa-ui/fetch";

import { LOGIN_FORM_TYPE, MFA_FORM_TYPE, RE_AUTHENTICATION_FORM_TYPE } from "../defines/formTypes";
import { getMfaErrorCode } from "../getMfaError";

/**
 * Intercepts responses that require action based on error code. Provider values to be used by AuthResponseDialog.
 */
export const AuthResponseHandlerContext = createContext();
export const AuthResponseHandler = (props) => {
  const { children } = props;
  const [activeForm, setActiveForm] = useState();
  const [logoutTrigger, triggerLogout] = useReducer((value) => value + 1, 0);
  const [shouldCleanUpSession, setShouldCleanUpSession] = useState(false);
  const [hasExistingAccountInfo, setHasExistingAccountInfo] = useState(false);
  const errorRules = useRef(getErrorRules());

  const fetchOptions = {
    retries: 1,
    retryOn: handleShouldRetry,
  };
  const pendingRetry = useRef(null);

  useEffect(() => {
    errorRules.current = getErrorRules();
  }, [hasExistingAccountInfo]);

  return (
    <FetchOptions options={fetchOptions}>
      <AuthResponseHandlerContext.Provider value={getContextValue()}>
        {children}
      </AuthResponseHandlerContext.Provider>
    </FetchOptions>
  );

  function getContextValue() {
    return {
      activeForm,
      logoutTrigger,
      pendingRetry: pendingRetry.current,
      setHasExistingAccountInfo,
      shouldCleanUpSession,
    };
  }

  async function handleShouldRetry(retryOps) {
    const { attempt, response } = retryOps;
    const errorCode = await getErrorCode(response);
    const matchingAction = errorRules.current.find(({ shouldHandle }) =>
      shouldHandle(errorCode)
    )?.action;
    let retry = false;

    if (matchingAction && attempt < 1) {
      retry = true;

      try {
        if (!pendingRetry.current) {
          pendingRetry.current = matchingAction(response?.url);
        }

        await pendingRetry.current.pendingAction;
      } catch (error) {
        retry = false;
      } finally {
        setActiveForm(null);
        pendingRetry.current = null;
      }
    }

    return retry;
  }

  function getErrorRules() {
    const {
      errorsTriggeringMfa,
      errorsTriggeringLogout,
      errorsTriggeringRelogin,
      errorsTriggeringRetry,
      errorsTriggeringReauthentication,
    } = getBootstrapData("api-gateway-errors") ?? {};

    return [
      {
        action: displayMfaDialogAsync,
        shouldHandle: matchesOneOf(errorsTriggeringMfa),
      },
      {
        action: displayReAuthDialogAsync,
        shouldHandle: matchesOneOf(errorsTriggeringReauthentication),
      },
      {
        action: hasExistingAccountInfo ? displayReAuthDialogAsync : displayLoginDialogAsync,
        shouldHandle: matchesOneOf(errorsTriggeringRelogin),
      },
      {
        action: cleanUpSessionAsync,
        shouldHandle: matchesOneOf(errorsTriggeringRetry),
      },
      {
        action: logoutAsync,
        shouldHandle: matchesOneOf(errorsTriggeringLogout),
      },
    ];
  }

  /**
   * Extracts error codes from API responses that can come in two formats:
   * On-Prem API GW:
   * {
   *   code: "401120722",
   *   ...
   * }
   *
   * Cloud API GW:
   * {
   *   errors: [{
   *     code: "401120722",
   *     ...
   *   }]
   * }
   */

  async function getErrorCode(response) {
    let errorCode = null;

    if (!isResponseOk(response)) {
      const json = (await getJson(response)) ?? {};
      /**
       * For authentication, we only need
       * to handle the first error since auth validation fails at the first failed rule.
       */
      const jsonError = getResponseErrors(json)?.[0];

      errorCode = /^\d+$/.test(jsonError?.code) ? jsonError.code : getMfaErrorCode(jsonError);
    }

    return errorCode;
  }

  async function getJson(response) {
    let json;

    try {
      json = await response.clone().json();
    } catch (e) {
      json = {};
    }

    return json;
  }

  function matchesOneOf(errorCodes) {
    return (errorCode) => errorCodes?.some((code) => `${code}` === `${errorCode}`);
  }

  function displayMfaDialogAsync(url) {
    const newRetryState = {};

    newRetryState.pendingAction = new Promise((resolve, reject) => {
      newRetryState.next = resolve;
      newRetryState.cancel = reject;
      newRetryState.url = url;
      setActiveForm({ type: MFA_FORM_TYPE });
    });

    return newRetryState;
  }

  function displayReAuthDialogAsync(url) {
    const newRetryState = {};

    newRetryState.pendingAction = new Promise((resolve, reject) => {
      newRetryState.next = resolve;
      newRetryState.cancel = reject;
      newRetryState.url = url;
      setActiveForm({ type: RE_AUTHENTICATION_FORM_TYPE });
    });

    return newRetryState;
  }

  function displayLoginDialogAsync(url) {
    const newRetryState = {};

    newRetryState.pendingAction = new Promise((resolve, reject) => {
      newRetryState.next = resolve;
      newRetryState.cancel = reject;
      newRetryState.url = url;
      setActiveForm({ type: LOGIN_FORM_TYPE });
    });

    return newRetryState;
  }

  function cleanUpSessionAsync() {
    setShouldCleanUpSession(true);

    return {
      pendingAction: Promise.resolve(),
    };
  }

  function logoutAsync() {
    triggerLogout();

    return {
      pendingAction: Promise.resolve(),
    };
  }
};

AuthResponseHandlerContext.displayName = "AuthResponseHandlerContext";
